/**
 * 261104 E1-P1 Bước 6 — trang toàn màn «Chuyển thể mới» (theo skill). Mỗi trang một lượt: mở lượt mới nhất của truyện (hoặc `?run=<id>`), chưa có thì hiện màn bắt đầu.
 * Mọi quyết định về màn nào hiện do BE (`displayState`) + vài id chỉ trình duyệt biết (xem `mockupStateOf`). Lệnh tốn tiền dùng `RequestMemo`: mạng lỗi ⇒ bấm lại dùng CÙNG requestId.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { skillAdaptationApi as api } from '../../../services/api/skillAdaptation';
import type { SkillBoundaryChoice, SkillChangeChoice, SkillConflictDecision, SkillConflictReplaceAll, SkillGate, SkillInputPatch, SkillRunProjection, SkillRunSummary } from '../../../types/skillAdaptation';
import { apiErrorText } from '../apiErrorText';
import { newRequestId } from '../workflowPrimaryPolicy';
import { Alert, StepBar } from './SkillParts';
import { SkillStartScreen, StartForm, UploadCtx } from './SkillStartScreen';
import { SkillGatesDone, SkillPrepareScreen } from './SkillPrepareScreen';
import { SkillGateScreen } from './SkillGateScreen';
import { SkillWritingScreen } from './SkillWritingScreen';
import { SkillDoneScreen } from './SkillDoneScreen';
import { READABLE_WRITING_STATES, RequestMemo, headerTitleOf, mockupStateOf, pickRunToOpen, screenOfState, shouldPollOf, statusLabelOf } from './skillAdaptUtils';
import { chaptersUrlOf, runToResumeOf, writingStatusLabelOf } from './skillWritingUtils';
import { sourceStoriesApi } from '../../../services/api';
import './skillAdapt.css';

const NO_UPLOAD: UploadCtx = { state: null, fileName: '', message: '' };
const NOT_EPUB = 'Không đọc được: đây không phải file EPUB (hoặc file bị hỏng).';
type Notice = { kind: 'bad' | 'ok' | 'blue'; text: string } | null;
const POLL_MS = 3000;

const SkillAdaptPage: React.FC = () => {
  const { storyId = '' } = useParams<{ storyId: string }>();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const [runs, setRuns] = useState<SkillRunSummary[]>([]);
  const [run, setRun] = useState<SkillRunProjection | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [upload, setUpload] = useState<UploadCtx>(NO_UPLOAD);
  const [busy, setBusy] = useState(false);
  const [viewGate, setViewGate] = useState<SkillGate | null>(null);
  const [openEp, setOpenEp] = useState<number | null>(null);   // E1-P2: tập đang mở ở khung đọc
  const [notice, setNotice] = useState<Notice>(null);
  const [memo] = useState(() => new RequestMemo(newRequestId));
  const alive = useRef(true);
  const runRef = useRef<SkillRunProjection | null>(null);
  runRef.current = run;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async (preferRunId?: string | null) => {
    setLoading(true); setLoadError(null);
    try {
      const list = await api.listRuns(storyId);
      if (!alive.current) return;
      setRuns(list);
      const target = pickRunToOpen(list, preferRunId ?? null);   // N1: lượt mới nhất CHƯA xong (không mở nhầm lượt đã GATES_DONE); toàn lượt đã xong ⇒ màn bắt đầu
      setRun(target ? await api.getRun(target) : null);
    } catch (e) { if (alive.current) setLoadError(apiErrorText(e, 'Không tải được Chuyển thể mới')); } finally { if (alive.current) setLoading(false); }
  }, [storyId]);
  useEffect(() => { void load(sp.get('run')); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps
  // N1: tên truyện ở tiêu đề (lỗi tải tên không chặn trang)
  const [storyName, setStoryName] = useState<string | null>(null);
  useEffect(() => { let on = true; sourceStoriesApi.getSourceStoryDetail(storyId).then((r) => { if (on) setStoryName(r?.data?.story?.name ?? null); }).catch(() => undefined); return () => { on = false; }; }, [storyId]);

  const refresh = useCallback(async (): Promise<void> => {
    const id = runRef.current?.id; if (!id) return;
    const next = await api.getRun(id); if (alive.current) setRun(next);
  }, []);

  // máy đang chạy / đang sửa ⇒ hỏi lại định kỳ (im lặng khi lỗi mạng thoáng qua)
  const ds = run?.displayState ?? '';
  const shouldPoll = shouldPollOf(run);   // chuẩn bị · máy đang sửa cổng · đang viết · đang ghi Chapters
  useEffect(() => { if (openEp !== null && !READABLE_WRITING_STATES.includes(ds)) setOpenEp(null); }, [ds, openEp]);   // rời màn viết ⇒ đóng khung đọc
  useEffect(() => {
    if (!shouldPoll) return undefined;
    const t = setInterval(() => { refresh().catch(() => undefined); }, POLL_MS);
    return () => clearInterval(t);
  }, [shouldPoll, refresh]);

  /** Chạy một lệnh: khoá nút, báo lỗi bằng tiếng Việt, giữ/bỏ requestId theo `RequestMemo`, rồi tải lại bản chiếu. */
  const act = useCallback(async (key: string | null, what: string, fn: () => Promise<unknown>): Promise<void> => {
    if (busy) return;
    setBusy(true); setNotice(null);
    try {
      await fn(); if (key) memo.settle(key, null);
      await refresh();
    } catch (e: any) {
      if (key) memo.settle(key, e);
      setNotice({ kind: 'bad', text: apiErrorText(e, what) });
      if (e?.response?.status === 409) refresh().catch(() => undefined);
    } finally { if (alive.current) setBusy(false); }
  }, [busy, memo, refresh]);

  const runId = run?.id ?? '';
  const onPickFile = async (file: File): Promise<void> => {
    if (busy) return;
    setNotice(null);
    if (!/\.epub$/i.test(file.name)) { setUpload({ state: 'error', fileName: file.name, message: NOT_EPUB }); return; }
    setUpload({ state: 'parsing', fileName: file.name, message: '' }); setBusy(true);
    try {
      const next = run && run.status === 'DRAFT' ? await api.replaceSource(run.id, file) : await api.createRun(storyId, file);
      if (!alive.current) return;
      setRun(next); setUpload(NO_UPLOAD);
      api.listRuns(storyId).then((l) => { if (alive.current) setRuns(l); }).catch(() => undefined);
    } catch (e: any) {
      const code = e?.response?.data?.code;
      setUpload({ state: 'error', fileName: file.name, message: code === 'EPUB_UNREADABLE' || e?.response?.status === 415 || e?.response?.status === 422 ? NOT_EPUB : apiErrorText(e, 'Tải file') });
    } finally { if (alive.current) setBusy(false); }
  };
  const onPatch = (p: SkillInputPatch): void => { void act(null, 'Lưu lựa chọn', async () => { const next = await api.patchInput(runId, p); if (alive.current) setRun(next); }); };
  const onStart = (form: StartForm): void => {
    const key = `start:${runId}`;
    void act(key, 'Bắt đầu', async () => { await api.patchInput(runId, form); await api.start(runId, memo.idFor(key)); });
  };
  const onPause = (): void => { void act(null, 'Tạm dừng', () => api.pause(runId)); };
  const onResume = (): void => { const key = `resume:${runId}`; void act(key, 'Tiếp tục', () => api.resume(runId, memo.idFor(key))); };
  const onRaiseCap = (): void => { const key = `resume:${runId}`; void act(key, 'Cho chạy tiếp', async () => { await api.raiseCap(runId); await api.resume(runId, memo.idFor(key)); }); };
  const onApprove = (gate: SkillGate, choice: unknown): void => {
    const rev = run?.documents[gate].current?.revision; if (rev === undefined) return;
    const key = `approve:${gate}:${rev}`;
    void act(key, 'Duyệt', async () => { await api.approve(runId, gate, { revision: rev, choice, requestId: memo.idFor(key) }); setViewGate(null); });
  };
  const onRewrite = (gate: SkillGate, instruction: string, choices?: Record<string, SkillChangeChoice>): void => {
    const rev = run?.documents[gate].current?.revision; if (rev === undefined || !instruction.trim()) return;
    const key = `rewrite:${gate}:${rev}:${instruction.trim()}`;
    void act(key, 'Sửa theo lời bạn', () => api.rewrite(runId, gate, { instruction: instruction.trim(), baseRevision: rev, requestId: memo.idFor(key), ...(choices ? { choices } : {}) }));
  };
  const onRegenerate = (gate: SkillGate): void => { const key = `regen:${runId}:${gate}`; void act(key, 'Tạo lại', () => api.regenerate(runId, gate, memo.idFor(key))); };
  const onResplit = (): void => {
    const rev = run?.documents.EPISODES.current?.revision; if (rev === undefined) return;
    const key = `resplit:${runId}:${rev}`;
    void act(key, 'Chia lại', () => api.resplit(runId, { baseRevision: rev, requestId: memo.idFor(key) }));
  };

  // ── E1-P2: viết kịch bản + đưa vào Chapters ──
  const onWrite = (): void => { const key = `write:${runId}`; void act(key, 'Viết kịch bản', () => api.write(runId, memo.idFor(key))); };
  const onBoundary = (n: number, choice: SkillBoundaryChoice): void => { const key = `boundary:${runId}:${n}:${choice}`; void act(key, 'Quyết định cho tập này', () => api.boundary(runId, n, { choice, requestId: memo.idFor(key) })); };
  const onStopEarly = (): void => { const key = `stop-early:${runId}`; void act(key, 'Dừng sớm', () => api.stopEarly(runId, memo.idFor(key))); };
  const loadEpisode = useCallback((n: number) => api.getEpisode(runId, n), [runId]);
  const loadComparison = useCallback((n: number) => api.getComparison(runId, n), [runId]);
  const loadFinalState = useCallback(() => api.getFinalState(runId), [runId]);
  const resolveConflicts = useCallback((body: { decisions?: SkillConflictDecision[]; keepAll?: boolean; replaceAll?: SkillConflictReplaceAll[] }) => api.resolveConflicts(runId, body), [runId]);
  const onChanged = useCallback((): void => { refresh().catch(() => undefined); }, [refresh]);

  const stateId = useMemo(() => mockupStateOf(run, { upload: upload.state, viewGate, openEp }), [run, upload.state, viewGate, openEp]);
  const screen = screenOfState(stateId);
  const openRun = (id: string): void => { setViewGate(null); setOpenEp(null); setNotice(null); setSp(id ? { run: id } : {}); void load(id || null); };
  const startNew = (): void => { setRun(null); setViewGate(null); setOpenEp(null); setNotice(null); setUpload(NO_UPLOAD); setSp({}); };
  const onBackToWriting = (): void => {
    const id = runToResumeOf(runs, runId);
    if (id) openRun(id); else setNotice({ kind: 'blue', text: 'Chưa có lượt viết nào đang dở. Bạn có thể mở lại danh sách các lượt ở phía trên.' });
  };

  const statusLabel = !run ? 'Chưa có nguồn' : run.status === 'GATES_DONE' ? 'Bốn quyết định đã chốt' : screen.screen === 'start' ? 'Chuẩn bị nguồn' : screen.screen === 'prepare' ? statusLabelOf(run, stateId) : screen.screen === 'writing' || screen.screen === 'done' ? writingStatusLabelOf(stateId, run) : stateId.endsWith('-approved') ? 'Đã chốt' : stateId.endsWith('-editing') ? 'Máy đang sửa' : 'Chờ bạn';
  const meta = run ? `Chương ${run.input.range.from}–${run.input.range.to} · Kịch bản ${run.input.outputLanguage === 'en' ? 'tiếng Anh' : 'tiếng Việt'}` : 'Từ nguyên tác đến kịch bản từng tập';

  let content: React.ReactNode;
  if (loading && !run) content = <div className="panel run"><span className="spin" aria-label="Đang tải" /><p className="muted">Đang tải Chuyển thể mới…</p></div>;
  else if (loadError && !run) content = <div className="panel run"><Alert kind="bad">{loadError}</Alert><button type="button" className="btn primary" onClick={() => void load(sp.get('run'))}>Thử lại</button></div>;
  else if (run && run.status === 'GATES_DONE' && !viewGate) content = <SkillGatesDone run={run} busy={busy} onView={setViewGate} onWrite={onWrite} />;
  else if (!run || screen.screen === 'start') content = <SkillStartScreen run={run} stateId={stateId} upload={upload} busy={busy} onPickFile={(f) => void onPickFile(f)} onPatch={onPatch} onStart={onStart} />;
  else if (screen.screen === 'prepare') content = <SkillPrepareScreen run={run} stateId={stateId} busy={busy} onPause={onPause} onResume={onResume} onRaiseCap={onRaiseCap} />;
  else if (screen.screen === 'writing') content = <SkillWritingScreen run={run} stateId={stateId} busy={busy} openEp={openEp} loadEpisode={loadEpisode} onOpenEp={setOpenEp} onPause={onPause} onResume={onResume} onRaiseCap={onRaiseCap} onBoundary={onBoundary} onStopEarly={onStopEarly} />;
  else if (screen.screen === 'done') content = <SkillDoneScreen key={`${run.id}-${stateId}`} run={run} stateId={stateId} busy={busy} loadComparison={loadComparison} loadFinalState={loadFinalState} resolve={resolveConflicts} onChanged={onChanged}
    onOpenChapters={() => navigate(chaptersUrlOf(storyId))} onResumePush={onResume} onBackToWriting={onBackToWriting} onViewDecisions={() => setViewGate('EPISODES')} />;
  else if (screen.screen === 'gate') {
    const g = screen.gate;
    content = <SkillGateScreen key={`${run.id}-${g}-${run.documents[g].current?.revision ?? 0}-${stateId.endsWith('-approved') ? 'v' : 'c'}`} run={run} gate={g} stateId={stateId} viewing={stateId.endsWith('-approved')} busy={busy}
      onApprove={(c) => onApprove(g, c)} onRewrite={(i, ch) => onRewrite(g, i, ch)} onRegenerate={() => onRegenerate(g)} onResplit={onResplit} onResume={onResume} onRaiseCap={onRaiseCap} onBackToCurrent={() => setViewGate(null)} />;
  } else content = <div className="panel run"><Alert kind="bad">Trạng thái này chưa được hỗ trợ ở bản hiện tại.</Alert><button type="button" className="btn primary" onClick={() => void refresh()}>Tải lại</button></div>;

  return (
    <div className="sk">
      <main className="sk-app">
        <div className="row between">
          <span className="crumb">Nội dung / Truyện nguồn / Chuyển thể</span>
          <span className="row"><button type="button" className="btn ghost" onClick={() => navigate(`/source-stories/${storyId}?mode=film&tab=adaptation`)}>Luồng cũ ↗</button></span>
        </div>
        <header className="row top">
          <span className="brand" aria-hidden="true" />
          <div className="grow"><div className="eyebrow">TaleBox · Chuyển thể mới</div><h1>{headerTitleOf(storyName)}</h1><p className="muted">{meta}</p></div>
          <span className="chip">{statusLabel}</span>
        </header>
        {runs.length > 0 ? (
          <details className="fd"><summary>Các lượt chuyển thể của truyện này ({runs.length})</summary>
            <div className="runlist">{runs.map((r) => (
              <div className="runitem" key={r.id}><b>{r.fileName}</b><span className="muted">ch.{r.range.from}–{r.range.to}</span><span className="chip">{new Date(r.modifiedAt).toLocaleString('vi-VN')}</span>
                <button type="button" className="btn sm" disabled={run?.id === r.id} onClick={() => openRun(r.id)}>{run?.id === r.id ? 'Đang mở' : 'Mở lượt này'}</button></div>))}
              <div><button type="button" className="btn sm" onClick={startNew}>Bắt đầu lượt mới từ file khác</button></div></div>
          </details>
        ) : null}
        <StepBar run={run} viewGate={viewGate} onView={setViewGate} />
        {notice ? <Alert kind={notice.kind}>{notice.text}</Alert> : null}
        <section aria-live="polite">{content}</section>
      </main>
    </div>
  );
};

export default SkillAdaptPage;
